import { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { ComboDataDTO } from '../../dto/ComboDataDTO';
import { downloadBenefitOrderPdf, getBillHospitalizationDetailLevels } from '../../servicesHooks/BillHospitalization/useBillHospitalizationService';
import { parseApiError } from '../../services/apiError';
import useNotify from '../../hooks/useNotify';
import './billHospitalization/novariumBill.scss';

/** "Todos": el PDF con el detalle de importes de cada línea (BudgetDetailLevel.DETAILBYITEM). */
const ALL_VALUES = 1;

interface BenefitOrderPdfDialogProps {
    budgetId: number;
    /** Orden a descargar; con null el diálogo está cerrado. */
    benefitOrderId: number | null;
    onHide: () => void;
}

/**
 * Pregunta qué valores mostrar antes de descargar el PDF de una orden, como el PDF de la factura sanatorial.
 * El destinatario no se pregunta: la orden ya es de la financiadora o del paciente.
 */
export default function BenefitOrderPdfDialog({ budgetId, benefitOrderId, onHide }: BenefitOrderPdfDialogProps) {
    const notify = useNotify();
    const [detailLevels, setDetailLevels] = useState<ComboDataDTO[]>([]);
    const [detailLevel, setDetailLevel] = useState<number>(ALL_VALUES);
    const [downloading, setDownloading] = useState(false);
    const visible = benefitOrderId != null;

    useEffect(() => {
        if (!visible) return;
        setDetailLevel(ALL_VALUES);
        let cancelled = false;
        // Los mismos textos que en el PDF de la factura: los da el backend
        getBillHospitalizationDetailLevels({ filterValue: '', offSet: 0, pageSize: 10 })
            .then((data: ComboDataDTO[]) => { if (!cancelled) setDetailLevels(data ?? []); })
            .catch((error: unknown) => {
                parseApiError(error);
                if (!cancelled) notify.error('No se pudieron cargar las opciones del PDF. Intentá de nuevo.');
            });
        return () => { cancelled = true; };
    }, [visible, notify]);

    async function handleDownload() {
        if (benefitOrderId == null) return;
        setDownloading(true);
        try {
            await downloadBenefitOrderPdf(budgetId, benefitOrderId, detailLevel);
            onHide();
        } catch (error: unknown) {
            parseApiError(error); // loguea errorCode y path para cruzarlo con el backend
            notify.error('No se pudo descargar el PDF. Intentá de nuevo.');
        } finally {
            setDownloading(false);
        }
    }

    const footer = (
        <>
            <Button type="button" className="buttonMain2" outlined label="Cancelar" onClick={onHide} disabled={downloading} />
            <Button type="button" className="buttonMain" icon="pi pi-download" label="Descargar" onClick={handleDownload} loading={downloading} />
        </>
    );

    return (
        <Dialog
            header={benefitOrderId != null ? `Descargar PDF de la orden N.° ${benefitOrderId}` : 'Descargar PDF'}
            visible={visible}
            onHide={() => { if (!downloading) onHide(); }}
            footer={footer}
            breakpoints={{ '640px': '100vw' }}
            className="nv-scope nv-dialog nv-dialog-s"
            modal
        >
            <div className="nv-dialog-body">
                <div className="nv-field">
                    <label htmlFor="benefit-order-pdf-values">Valores a mostrar</label>
                    <Dropdown
                        inputId="benefit-order-pdf-values"
                        value={detailLevel}
                        options={detailLevels}
                        optionLabel="name"
                        optionValue="id"
                        onChange={(e) => setDetailLevel(e.value)}
                    />
                </div>
            </div>
        </Dialog>
    );
}
